/**
 * SOLID Review
 * S — Strong. One concern: which screen the tab shows before the game, and the one session start a page load makes — with what its
 *     link asked for.
 * O — Good. A new thing a link may ask is one more read of the address.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The view-model surface; nothing else.
 * D — Strong. The client view and one use case (the Application's ports), the request contexts, the page's address role.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import type { IPortClientStateView, IEntitySessionView } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { ClientRequests } from '../client/ClientRequests';
import type { IServicePageAddress } from './platform/IConfigDepViewPlatform';

interface IStateAuthRouter {
  readonly screen: IEntitySessionView['screen'];
}

export interface IConfigDepAuthRouter {
  readonly view: IPortClientStateView;
  readonly useCases: Pick<IPortClientUseCases, 'startSession'>;
  readonly requests: ClientRequests;
  readonly address: IServicePageAddress;
}

/**
 * THE AUTH ROUTER (frontend-framework-oo P1, 2026-10-29, §28.293) — App.tsx's `AuthRouter`, whose session start ran in an effect
 * guarded by a module-level flag: React's StrictMode double-invokes a mount's effects, and the first run strips `?join=`/`?payment=`
 * from the address, so a second run would start the session again from an empty query and clobber the first. The flag is this
 * object's field now, and the object lives as long as the page: the `ViewModelFactory` builds one.
 *
 * Its snapshot is the screen, read from the client view — the same object until the screen changes.
 */
export class AuthRouterViewModel implements IServiceViewModel<IStateAuthRouter> {
  private started = false;
  private state: IStateAuthRouter;

  constructor(private readonly deps: IConfigDepAuthRouter) {
    this.state = { screen: deps.view.getSnapshot().session.screen };
  }

  getSnapshot(): IStateAuthRouter {
    const screen = this.deps.view.getSnapshot().session.screen;
    if (screen !== this.state.screen) this.state = { screen };
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    return this.deps.view.subscribe(listener);
  }

  /**
   * The page's one session start. What the URL asked for — Stripe's return, or a `?join=<code>` link (the same smart join as the
   * funnel's code) — is read here and stripped, so a reload does not repeat it; who the tab is and where it lands are the session's
   * (strict-ports FE2).
   */
  attach(): void {
    if (this.started) return;
    this.started = true;
    const params = new URLSearchParams(this.deps.address.search());
    const paymentReturn = params.get('payment') === 'success';
    const joinCode = paymentReturn ? null : params.get('join');
    if (paymentReturn || joinCode) this.deps.address.replace(this.deps.address.pathname());
    const intent: TClientUseCaseInput<'startSession'> = { joinCode, paymentReturn };
    void this.deps.useCases.startSession(this.deps.requests.context(), intent);
  }

  /** The start is the page's, not the view's: leaving the screen undoes nothing. */
  detach(): void {}
}
